import { AfterViewInit, Component, ElementRef, HostBinding, OnInit, QueryList, ViewChild, ViewChildren, ViewEncapsulation, OnDestroy } from '@angular/core';
import { formatDate } from 'ngx-bootstrap/chronos';
import * as _ from 'underscore';
import * as jquery from 'jquery';
import { isObject, isArray, isString } from '../../models/utils';
import { Corpus } from '../../models/corpus';
import { DeferredPromise } from '../../models/deferred-promise';
import { Endpoint, EntityType, HelpIndex, ModalTargetType, TagOperationType, SearchType, ReviewSystem } from '../../models/enums';
import { NavigationContentComponent } from '../../models/navigation-content-component';
import { ObjectActionListType, Permissions, ActionType } from '../../models/permission';
import { Report, ReportDisplayType, ReportMap, ReportSearchType, ReportType, WorkflowReport } from '../../models/report';
import { DetailsType } from '../../models/report-details';
import { SearchResult } from '../../models/search-result';
import { ViewType, View } from '../../models/view';
import { CommonService } from '../../services/common/common.service';
import { RestService } from '../../services/rest/rest.service';
import { XMLService } from '../../services/xml/xml.service';
import { GenericTreeNode } from '../generic-tree/generic-tree-node';
import { GenerateMoreReportsModalComponent } from '../modals/generate-more-reports-modal/generate-more-reports-modal.component';
import { GenerateDataSetReportModalComponent, GenerateDataSetReportModalData } from '../modals/generate-data-set-report-modal/generate-data-set-report-modal.component';
import { ReportsDetailsModalComponent, ReportsDetailsModalData } from '../modals/reports-details-modal/reports-details-modal.component';
import { RunCombinedSearchModalComponent } from '../modals/run-combined-search-modal/run-combined-search-modal.component';
import { TagsModalComponent, TagsModalData } from '../modals/tags-modal/tags-modal.component';
import { ReportPanelComponent } from '../report-panel/report-panel.component';
import { Comparison } from '../../models/comparison';
import { DeDupScope } from '../../models/analytical-settings';
import { SavedSearch } from '../../models/saved-searches';
import { WorkingSet } from '../../models/working-set';
import { Custodian } from '../../models/custodian';
import { DynamicFolder } from '../../models/dynamic-folder';
import { WorkflowStep } from '../../models/workflow';
import { interval } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { MediaID } from '../../models/mediaid';
import { Batch } from '../../models/batch';
import { Tag } from '../../models/tag';

@Component({
    standalone: false,
    selector: 'res-reports',
    templateUrl: './reports.component.html',
    styleUrls: ['./reports.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class ReportsComponent implements NavigationContentComponent, AfterViewInit, OnInit, OnDestroy {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChildren(ReportPanelComponent) reportPanels: QueryList<ReportPanelComponent>;
    @ViewChild('downloadLink', { read: ElementRef, static: true }) downloadLink: ElementRef;

    isBusy = false;
    isBusyMessage: string;
    node: GenericTreeNode;
    reports: any[];
    reportList: any;
    reportType = ReportType;
    oldestReportDate: string;
    searchSummarySearchType: string;
    entityTypes = EntityType;
    detailTypes = DetailsType;
    exportComparisonSearchNode: string;
    exportComparisonExportNode: string;
    reportsVisibleList = [];
    reportsDisabledList = null;
    workflow: any;
    handle: string;
    readonly dashboardName = 'Dashboard';
    hasAuditPermission: boolean;
    hasPreferencesPermission: boolean;
    hasExportPermission: boolean;
    hasDownloadPermission: boolean;
    hasTagsPermission: boolean;
    reportGenerationInProgress: boolean;
    getReportPoller: any;
    exceptionReportsInProgress: boolean;
    isShortMessageTypeSlack: boolean;

    summaryReport: ReportType;
    classificationReport: ReportType;
    idxExceptionGeneratedReports = 0;
    loadingReportsIsBusy: Map<ReportType, boolean>;
    allLoadedReports: any;

    constructor(private commonService: CommonService, private restService: RestService, private xmlService: XMLService) { }

    ngOnInit(): void {
        this.hasAuditPermission = Permissions.checkUserPermission(ObjectActionListType.AUDIT_LOG);
        this.hasPreferencesPermission = Permissions.checkUserPermission(ObjectActionListType.PROJECT_PREFERENCES, ActionType.CREATE);
        this.hasExportPermission = Permissions.checkUserPermission(ObjectActionListType.EXPORT_STREAM);
        this.hasDownloadPermission = Permissions.checkUserPermission(ObjectActionListType.DOCUMENT_REPORTS);
        this.hasTagsPermission = Permissions.checkUserPermission(ObjectActionListType.DOCUMENT_TAGS, ActionType.CREATE);
    }

    ngAfterViewInit(): void {
        this.reportPanels.changes.subscribe((panels: QueryList<ReportPanelComponent>) => {
            return;
        });
    }

    selectedTreeItemChanged = (node: GenericTreeNode): void => {
        this.reports = null;
        this.node = node;
        if (node && this.commonService.projectData) {
            this.handle = node.data && node.data.view ? node.data.view.handle : node.data ? node.data.handle : this.commonService.projectData.handle;
            this.isShortMessageTypeSlack = this.node?.data?.attributes?.some(a =>
                (a.name === "shortMessageType" || a.name === "shortMesageType") && a.value === "SLACK"
            ) || false;
            this.workflow = null;
            this.loadData();
        }
    }

    update(): void {

        this.isBusy = true;
        this.isBusyMessage = 'Updating Reports ...';
        this.stopReportsPoller();
        this.restService.post(Endpoint.viewManager_invalidateReportCache, { viewHandles: [this.handle] }).then(response => {
            if (response) {
                this.isBusy = false;
                if (this.node && this.node.name === this.dashboardName && !this.commonService.dashboardReportsEnabled) {
                    this.updateDashboardReportsEnabled();
                } else {
                    this.reports = null;
                    this.loadData();
                }
            }
            this.isBusy = false;
        }, error => {
            this.isBusy = false;
        });
    }

    setAnalysisReportTypes(): void {
        this.summaryReport = this.commonService.currentProject.deDupScope === DeDupScope.VERTICAL ?
            ReportType.TERM_SEARCH_HIT_COUNT_VERTICAL : ReportType.TERM_SEARCH_HIT_COUNT;
        this.classificationReport = this.commonService.currentProject.deDupScope === DeDupScope.VERTICAL ?
            ReportType.TECHNICAL_REDUCTION_VERTICAL : ReportType.TECHNICAL_REDUCTION;
    }

    getViewTypeFromAttributes(searchResult: any): string {
        if (searchResult && searchResult.attributes) {
            const viewTypeAttrib = searchResult.attributes.find(a => a.name === 'viewtype');
            if (viewTypeAttrib) {
                return viewTypeAttrib.value;
            }
        }
        return null;
    }

    getVisibleReports(): ReportType[] {  // Not set up for use with export volumes or workflows
        let visibleReports = [];
        const sample = this.node.data && this.node.data.attributes ? this.node.data.attributes.find(a => a.name === 'sampleSetResult') : null;
        const isSample = sample && sample.value === 'true';

        if (this.node && this.node.data && this.node.data.task === SearchType.SEARCH_FROM_BULK_SEARCH) {
            this.node.data.viewType = this.getViewTypeFromAttributes(this.node.data);
        }

        if (this.node && this.node.name === this.dashboardName) { // Dashboard
            visibleReports = [this.classificationReport, ReportType.FILETYPE, ReportType.DATE_EMAIL_SENT];
        } else if (this.node.entityType === EntityType.import && this.node.data instanceof Corpus) { // Data Set (aka individual import)
            visibleReports = [ReportType.IMPORT,
            ReportType.IMPORT_ERROR, ReportType.CUSTOM_WARNINGS_ERRORS, ReportType.OCR_CANDIDATES, ReportType.OCR_CONFIDENCE_LEVEL,
            this.classificationReport, ReportType.BILLING_REPORT, ReportType.DOCUMENT_CLASS, ReportType.FILETYPE,
            ReportType.CUSTODIAN_DIRECTORY_REPORT, ReportType.CORPUS_SOURCE, ReportType.EMAIL_SENT_DOMAINS, ReportType.EMAIL_RECEIVED_DOMAINS,
            ReportType.DOMINANT_LANGUAGE, ReportType.LANGUAGE, ReportType.DATE_EMAIL_SENT, ReportType.DATE_EMAIL_RECEIVED, ReportType.EMAIL_SENDER,
            ReportType.EMAIL_RECEIVER, ReportType.REVIEW_FOLDER];
        } else if (this.node.entityType === EntityType.import) { // Imports top-level node
            visibleReports = [ReportType.IMPORT_ERROR, ReportType.CUSTOM_WARNINGS_ERRORS, ReportType.OCR_CANDIDATES, ReportType.OCR_CONFIDENCE_LEVEL,
            ReportType.OCR_SOURCE, this.classificationReport, ReportType.BILLING_REPORT, ReportType.DOCUMENT_CLASS, ReportType.FILETYPE,
            ReportType.CUSTODIAN_DIRECTORY_REPORT, ReportType.CORPUS_SOURCE, ReportType.EMAIL_SENT_DOMAINS, ReportType.EMAIL_RECEIVED_DOMAINS,
            ReportType.DOMINANT_LANGUAGE, ReportType.LANGUAGE, ReportType.DATE_EMAIL_SENT, ReportType.DATE_EMAIL_RECEIVED, ReportType.EMAIL_SENDER,
            ReportType.EMAIL_RECEIVER, ReportType.REVIEW_FOLDER];
        } else if (this.node.data instanceof WorkingSet) { // Project Data
            visibleReports = [this.classificationReport, ReportType.BILLING_REPORT, ReportType.DOCUMENT_CLASS, ReportType.FILETYPE,
            ReportType.OCR_CONFIDENCE_LEVEL, ReportType.GROUP_VIEW_MEMBERSHIP, ReportType.CORPUS_SOURCE, ReportType.TAG_SUMMARY,
            ReportType.EMAIL_SENT_DOMAINS, ReportType.EMAIL_RECEIVED_DOMAINS, ReportType.DOMINANT_LANGUAGE, ReportType.LANGUAGE,
            ReportType.DATE_EMAIL_SENT, ReportType.DATE_EMAIL_RECEIVED, ReportType.DATE_PRIMARY, ReportType.EMAIL_SENDER,
            ReportType.EMAIL_RECEIVER, ReportType.REVIEW_FOLDER];
        } else if (this.node.data instanceof SearchResult && this.node.data.exportComparison) { // Export Comparison Report
            if ((this.node.data as any).searchTermReportRequested) {
                visibleReports = [ReportType.EXPORT_COMPARE, ReportType.EXPORT_COMPARE_STATISTICS, ReportType.SEARCH, this.summaryReport,
                this.classificationReport, ReportType.DOCUMENT_CLASS, ReportType.FILETYPE,
                ReportType.OCR_CONFIDENCE_LEVEL, ReportType.GROUP_VIEW_MEMBERSHIP, ReportType.CORPUS_SOURCE, ReportType.TAG_SUMMARY,
                ReportType.EMAIL_SENT_DOMAINS, ReportType.EMAIL_RECEIVED_DOMAINS, ReportType.DOMINANT_LANGUAGE, ReportType.LANGUAGE,
                ReportType.DATE_EMAIL_SENT, ReportType.DATE_EMAIL_RECEIVED, ReportType.DATE_PRIMARY, ReportType.EMAIL_SENDER,
                ReportType.EMAIL_RECEIVER, ReportType.REVIEW_FOLDER];
            } else {
                visibleReports = [ReportType.EXPORT_COMPARE, ReportType.EXPORT_COMPARE_STATISTICS];
            }
        } else if (this.node.data && this.node.data.task === SearchType.BULK) { // Bulk search
            visibleReports.push(ReportType.SEARCH);
        } else if (((this.node.data instanceof SearchResult || this.node.data instanceof WorkflowStep) &&
            this.node.data.viewType === ViewType.WORKING_SET_RESULT) || // Project Data search
            (this.node.data instanceof SavedSearch) || // Project Data saved search
            (isSample)) {
            visibleReports = [ReportType.SEARCH, this.summaryReport, this.classificationReport, ReportType.DOCUMENT_CLASS, ReportType.FILETYPE,
            ReportType.OCR_CONFIDENCE_LEVEL, ReportType.GROUP_VIEW_MEMBERSHIP, ReportType.CORPUS_SOURCE, ReportType.TAG_SUMMARY,
            ReportType.EMAIL_SENT_DOMAINS, ReportType.EMAIL_RECEIVED_DOMAINS, ReportType.DOMINANT_LANGUAGE, ReportType.LANGUAGE,
            ReportType.DATE_EMAIL_SENT, ReportType.DATE_EMAIL_RECEIVED, ReportType.DATE_PRIMARY, ReportType.EMAIL_SENDER,
            ReportType.EMAIL_RECEIVER, ReportType.REVIEW_FOLDER];
        } else if (((this.node.data instanceof SearchResult || this.node.data instanceof WorkflowStep) &&
            this.node.data.viewType === ViewType.CORPUS_SET_RESULT) ||    // Imports search
            (this.node.data instanceof SearchResult && this.node.data.viewType === ViewType.CORPUS_RESULT)) {    // Data set search
            visibleReports = [ReportType.SEARCH, ReportType.MAG_HIT_COUNT, ReportType.EXPORT_COMPARE,
            this.summaryReport, this.classificationReport, ReportType.DOCUMENT_CLASS, ReportType.FILETYPE,
            ReportType.OCR_CONFIDENCE_LEVEL, ReportType.CUSTODIAN_DIRECTORY_REPORT, ReportType.CORPUS_SOURCE,
            ReportType.EMAIL_SENT_DOMAINS, ReportType.EMAIL_RECEIVED_DOMAINS, ReportType.DOMINANT_LANGUAGE, ReportType.LANGUAGE,
            ReportType.DATE_EMAIL_SENT, ReportType.DATE_EMAIL_RECEIVED, ReportType.EMAIL_SENDER,
            ReportType.EMAIL_RECEIVER, ReportType.REVIEW_FOLDER];
        } else if (this.node.data instanceof Custodian || // Custodian
            this.node.data instanceof MediaID || // MediaID
            this.node.data instanceof Batch || // Batch
            this.node.data instanceof Comparison || // Comparison
            this.node.data instanceof DynamicFolder || // Folder
            (this.node.data instanceof View && this.node.data.viewType === ViewType.TAG)) { // Tag
            visibleReports = [this.classificationReport, ReportType.DOCUMENT_CLASS, ReportType.FILETYPE,
            ReportType.OCR_CONFIDENCE_LEVEL, ReportType.GROUP_VIEW_MEMBERSHIP, ReportType.CORPUS_SOURCE, ReportType.TAG_SUMMARY,
            ReportType.EMAIL_SENT_DOMAINS, ReportType.EMAIL_RECEIVED_DOMAINS, ReportType.DOMINANT_LANGUAGE, ReportType.LANGUAGE,
            ReportType.DATE_EMAIL_SENT, ReportType.DATE_EMAIL_RECEIVED, ReportType.DATE_PRIMARY, ReportType.EMAIL_SENDER,
            ReportType.EMAIL_RECEIVER, ReportType.REVIEW_FOLDER];
        }
        if (this.node.data && this.node.data.task === SearchType.METADATA_BULK) { // Meatadata Bulk search
            visibleReports.push(ReportType.METADATA_BULK_SEARCH_SUMMARY);
        }
        return visibleReports;
    }

    loadData(): void {
        let request;
        this.setAnalysisReportTypes();
        this.reportsVisibleList = this.getVisibleReports();
        this.reportsDisabledList = null;
        if (this.node && this.node.name === this.dashboardName) {
            this.loadDataForDashboard();
        } else if (this.node && this.node.data && this.node.data.task === SearchType.BULK) {
            this.reportsVisibleList = this.getSearchReportListType(this.node);
            this.getReport(this.formatReportRequest(this.reportsVisibleList));
        } else {
            this.isBusy = true;
            this.isBusyMessage = 'Loading Report Settings...';
            this.restService.post(Endpoint.projectManager_getReportSettings, { handle: this.commonService.currentProject.handle }).then(responseAllFields => {
                this.isBusy = false;
                if (responseAllFields && responseAllFields.reportGenerationProfileDto && responseAllFields.reportGenerationProfileDto.reportGenerationProfileEntries) {
                    const listType = this.getReportListTypeFromEntityType(this.node.entityType);
                    this.reportList = _.find(responseAllFields.reportGenerationProfileDto.reportGenerationProfileEntries, listTypes => listTypes.reportCategory === listType);
                    if (this.reportList && this.reportList.reports) {
                        this.reportList.reports = _.uniq(this.reportList.reports);
                        this.reportsDisabledList = this.getDisabledReports(this.reportList.reports);
                    }
                    if (this.reportList || this.node.entityType === EntityType.workFlow || this.node.entityType === EntityType.searchResult || this.node.entityType === EntityType.savedSearch) {
                        if (this.node && this.node.entityType === EntityType.exportVolume) {
                            this.reportsVisibleList = [ReportType.EXPORT_LOAD_FILE_REPORT, ReportType.EXPORT_PRODUCE_REPORT, ReportType.EXPORT_PREPARE_REPORT];
                            request = this.formatReportRequest(this.reportsVisibleList);
                        } else {
                            request = this.formatReportRequest(this.reportList.reports);
                        }

                        if (this.node.entityType === EntityType.workFlow) {
                            request = this.formatReportRequest([ReportType.WORKFLOW]);
                            this.getWorkflow(request);
                        } else {
                            if (this.node.entityType === EntityType.searchResult || this.node.entityType === EntityType.savedSearch) {
                                // this.reportList.reports is only enabled reports, minus the special search ones
                                if (this.node.data instanceof SearchResult && this.node.data.viewType === ViewType.CORPUS_RESULT) {
                                    this.reportList.reports = _.filter(this.reportList.reports, (report) =>
                                        report !== ReportType.IMPORT && report !== ReportType.IMPORT_ERROR &&
                                        report !== ReportType.BILLING_REPORT
                                    );
                                }
                                if (this.node.data instanceof SearchResult && this.node.data.viewType === ViewType.CORPUS_SET_RESULT) {
                                    this.reportList.reports = _.filter(this.reportList.reports, (report) =>
                                        report !== ReportType.IMPORT_ERROR && report !== ReportType.OCR_SOURCE);
                                }
                                let searchReports = _.union(this.reportList.reports,
                                    [ReportType.SEARCH, ReportType.MAG_HIT_COUNT, ReportType.EXPORT_COMPARE,
                                    ReportType.TERM_SEARCH_HIT_COUNT]);
                                // only the information of EXPORt_COMPARE is needed when reports are loaded for export
                                // comparisons when 'Include Search Term Report' option is unchecked
                                if (this.node.data && this.node.data.exportComparison && !this.node.data.searchTermReportRequested) {
                                    searchReports = [ReportType.EXPORT_COMPARE];
                                }
                                if (this.node.data.task === SearchType.METADATA_BULK) {
                                    this.reportsVisibleList = this.getSearchReportListType(this.node);
                                    searchReports = _.union(this.reportList.reports, [ReportType.METADATA_BULK_SEARCH_SUMMARY]);
                                }
                                request = this.formatReportRequest(searchReports);
                                if (searchReports.find(r => r === ReportType.EXPORT_COMPARE)) {
                                    searchReports = _.union(searchReports, [ReportType.EXPORT_COMPARE_STATISTICS]);
                                }
                                this.reportsDisabledList = this.getDisabledReports(searchReports);
                            }

                            this.getReport(request);
                        }
                    } else {
                        this.isBusy = false;
                    }
                }
            }, error => {
                this.isBusy = false;
            });
        }
    }

    getDisabledReports(reportsToLoad: any[]): any[] {
        // reportsToLoad as per the backend response may only have TECHNICAL_REDUCTION and TECHNICAL_REDUCTION for both 
        // document classification global or custodial - need to update the reportsToLoad accordingly
        reportsToLoad = reportsToLoad.map(reportToLoadName => {
            const matchedReport = this.reportsVisibleList.find(reportName => reportName.startsWith(reportToLoadName));
            return matchedReport ? matchedReport : reportToLoadName;
        });
        if (this.reportsVisibleList && reportsToLoad) {
            return this.reportsVisibleList.filter(function (repType) { return reportsToLoad.indexOf(repType) < 0; });
        }
        return null;
    }

    loadDataForDashboard(): void {
        setTimeout(() => {
            if (this.commonService.isGetPreferencesResponse && this.commonService.currentProject?.deDupScope) {
                this.setAnalysisReportTypes();
                this.reportsVisibleList = this.getVisibleReports();
                if (this.commonService.dashboardReportsEnabled) {
                    this.getReport(this.formatReportRequest(this.reportsVisibleList));
                } else {
                    this.reports = [ReportMap.get(ReportType[ReportType[ReportType.FILETYPE]])(null, this.node),
                    ReportMap.get(ReportType[ReportType[this.classificationReport]])(null, this.node),
                    ReportMap.get(ReportType[ReportType[ReportType.DATE_EMAIL_SENT]])(null, this.node)];
                }
            } else {
                this.loadDataForDashboard();
            }
        }, 200);
    }

    selectedItems(): string {
        if (this.node.entityType === EntityType.searchResult && this.node.data && this.node.data.task === SearchType.BULK && this.reportPanels) {
            const bulkSearchGrid = this.reportPanels.first;
            if (bulkSearchGrid && bulkSearchGrid.reportGrid) {
                const count = bulkSearchGrid.reportGrid.selectedRows.length;
                if (count > 0) {
                    if (bulkSearchGrid.reportGrid.allRowsSelected) {
                        return `All ${count.toLocaleString()}`;
                    }
                    return `${count.toLocaleString()} on this page`;
                }
                return `${count.toLocaleString()}`;
            }
        }
        return '0';
    }

    getWorkflow(request: any): void {
        this.isBusy = true;
        this.isBusyMessage = 'Loading Workflow...';
        this.restService.post(Endpoint.workFlowManager_getWorkFlow, { handle: this.node.data.handle }).then(response => {
            this.isBusy = false;
            if (response && response.workFlow) {
                const workflowExecutionHandle = _.find(response.workFlow.attributes, (attribute) => attribute.name === 'lastExecutionHandle');
                if (workflowExecutionHandle) {
                    const workflowRequest = _.clone(request);
                    workflowRequest.viewHandle = workflowExecutionHandle.value;
                    this.restService.post(Endpoint.viewManager_getReport, workflowRequest).then(responseWorkflowReport => {
                        this.initWorkflow(response.workFlow, request, responseWorkflowReport);
                    }, error => {
                        this.isBusy = false;
                        this.initWorkflow(response.workFlow, request, null);
                    });
                } else {
                    this.initWorkflow(response.workFlow, request, null);
                }
            }
        }).catch(error => {
            this.isBusy = false;
        });
    }

    initWorkflow(workflow: any, request: any, workflowReport: any): void {
        this.isBusy = true;
        this.isBusyMessage = 'Loading Workflow...';
        const tagPromise = new DeferredPromise<any>();
        const folderPromise = new DeferredPromise<any>();
        if (isObject(workflow)) {
            workflow = new WorkflowReport(workflow);

            // set queries
            if (workflow.queryItems.length > 0) {
                for (let i = 0; i < workflow.queryItems.length; i++) {
                    workflow.queryItems[i].resultCount = null;
                    workflow.queryItems[i].parsedQuery = this.xmlService.xmlToQueryConverter(workflow.queryItems[i].query);
                    if (workflow.queryItems[i].attributes && workflow.queryItems[i].responseHandle) {
                        const att = _.filter(workflow.queryItems[i].attributes, (w) => w.name === 'noOfResults');
                        if (att && att.length > 0) {
                            workflow.queryItems[i].resultCount = att.value;
                            workflow.lastRanQuery = workflow.queryItems[i];
                        }
                    }
                }
            }

            // set tags
            let workflowTags;
            if (workflowReport && workflowReport.reports && workflowReport.reports[0]) {
                workflowTags = _.find(workflowReport.reports[0].attributes, (attribute) => attribute.name === 'tagnames');
            } else if (workflow.tagItem && isArray(workflow.tagItem.tags)) {
                workflowTags = workflow.tagItem.tags;
            } else {
                tagPromise.resolve();
            }

            if (workflowTags) {
                this.restService.post(Endpoint.workingSetManager_listWorkingSetViews, {
                    workingSetHandle: this.commonService.projectData.handle,
                    count: 0,
                    startIndex: 0,
                    filters: [{
                        attribute: 'viewType',
                        value: 'TAG',
                        operator: 'EQUALS'
                    }]
                }).then(response => {
                    if (response && isArray(response.views)) {
                        const tagList = (<Array<any>>response.views).map(t => new Tag(t));
                        workflow.tagItem.taggedEntityList = [];
                        if (workflowReport && workflowReport.reports) {
                            const workflowReportTagList = workflowTags.value.split(';');
                            workflowReportTagList.forEach((entry, index) => {
                                const tag = _.filter(tagList, (w) => w.name === entry);
                                if (tag && tag.length > 0) {
                                    workflow.tagItem.taggedEntityList.push(tag[0]);
                                }
                            });
                        } else {
                            workflow.tagItem.tags.forEach((entry) => {
                                const tag = _.filter(tagList, (w) => w.handle === entry);
                                if (tag && tag.length > 0) {
                                    workflow.tagItem.taggedEntityList.push(tag[0]);
                                }
                            });
                        }
                    }
                    tagPromise.resolve();
                }, error => {
                    tagPromise.resolve();
                });
            } else {
                tagPromise.resolve();
            }

            this.restService.post(Endpoint.projectManager_listDynamicFolders, {
                count: 100,
                projectHandle: this.commonService.currentProject.handle,
                startIndex: 0
            }).then(response => {
                if (response.dynamicFolders && response.dynamicFolders.length > 0 && workflow.folderItem) {
                    const folder = _.filter(response.dynamicFolders, (f) => f.handle === workflow.folderItem.handle);
                    if (folder && folder.length > 0) {
                        workflow.folderItem.displayValue = folder[0].color;
                    }
                }
                folderPromise.resolve();
            }, error => {
                folderPromise.resolve();
            });

            this.workflow = workflow;

            if (workflow.lastExecutionHandle && !workflow.isRunning) {
                request.viewHandle = workflow.lastExecutionHandle;
                this.getReport(request);
            } else {
                this.reports = [ReportMap.get(ReportType[ReportType[ReportType.WORKFLOW]])(new Report(), this.node)];
            }

            Promise.all([tagPromise.promise, folderPromise.promise]).then(() => {
                this.isBusy = false;
            });
        }
    }

    workflowButtonsEnabled(): boolean {
        return Boolean(!this.workflow || (this.workflow && !this.workflow.lastExecutionHandle && this.workflow.resultCount !== '0') || (this.workflow && this.workflow.isRunning));
    }

    bulkSearchDownloadButtonEnabled(): boolean {
        return Boolean(this.node.entityType === EntityType.searchResult && this.node.data && this.node.data.task === SearchType.BULK && this.reportPanels);
    }

    getReport(request: any, withoutIsBusy = false): void {
        if (!withoutIsBusy) {
            this.isBusy = true;
            this.allLoadedReports = null;
            if (this.node.data && this.node.data.task === SearchType.BULK) {
                this.isBusyMessage = 'Loading Bulk Search Report...';
            } else {
                this.isBusyMessage = 'Generating Reports...';
            }
        }
        const exceptionDetailsDependantReports = this.reportsVisibleList ? this.reportsVisibleList.filter(rt => rt === ReportType.FILETYPE || rt === ReportType.IMPORT_ERROR) : null;
        this.exceptionReportsInProgress = exceptionDetailsDependantReports && exceptionDetailsDependantReports.length > 0;
        this.restService.post(Endpoint.viewManager_getReport, request).then(response => {
            if (response.taskHandle) {
                if (!withoutIsBusy) {
                    this.loadingReportsIsBusy = new Map<ReportType, boolean>();
                    if (this.reportsVisibleList) {
                        let reportIsBusy = true;
                        this.reportsVisibleList.forEach(r => {
                            if (this.reportsDisabledList && this.reportsDisabledList.length > 0) {
                                reportIsBusy = this.reportsDisabledList.indexOf(r) === -1;
                            }
                            this.loadingReportsIsBusy.set(r, reportIsBusy);
                        });
                    }
                }
                this.reportGenerationInProgress = true;
                if (response.reports && response.reports.length > 0) {
                    if (!this.allLoadedReports) {
                        this.allLoadedReports = [];
                    }
                    this.isBusy = false;
                    response.reports.forEach(report => {
                        request.reportRequests = request.reportRequests.filter(r => r.reportType !== report.reportType);
                        this.loadingReportsIsBusy.set(report.reportType, false);
                        if (!this.allLoadedReports.find(rep => rep.reportType === report.reportType)) {
                            this.allLoadedReports.push(report);
                        }
                    });

                    if (exceptionDetailsDependantReports && exceptionDetailsDependantReports.length > 0) {
                        const exceptionRepsGenerated = response.reports.filter(rt => rt === ReportType.FILETYPE || rt === ReportType.IMPORT_ERROR);
                        if (exceptionRepsGenerated) {
                            if (exceptionRepsGenerated.length === exceptionDetailsDependantReports.length || this.idxExceptionGeneratedReports === exceptionDetailsDependantReports.length) {
                                this.exceptionReportsInProgress = false;
                            } else if (exceptionRepsGenerated.length === 1 && exceptionDetailsDependantReports.length === 2) {
                                this.idxExceptionGeneratedReports++;
                            }
                        }
                    }
                    this.importReports(this.allLoadedReports);
                }
                if (!this.getReportPoller) {
                    this.startReportsPoller(request);
                }
            } else {
                this.reportGenerationInProgress = false;
                this.exceptionReportsInProgress = false;
                this.idxExceptionGeneratedReports = 0;
                this.stopReportsPoller();
                if (!response.reports) {
                    this.restService.post(Endpoint.viewManager_getReport, request).then(delayedResponse => {
                        this.processFinalReportData(delayedResponse.reports);
                    });
                } else {
                    this.processFinalReportData(response.reports);
                }
            }
        }, () => {
            this.stopReportsPoller();
            this.resetIsBusyForReports();
            this.reportGenerationInProgress = false;
            this.exceptionReportsInProgress = false;
            this.idxExceptionGeneratedReports = 0;
            this.isBusy = false;
        });
    }

    processFinalReportData(reports: any): void {
        if (this.allLoadedReports) {
            reports.forEach(r => {
                if (!this.allLoadedReports.find(rep => rep.reportType === r.reportType)) {
                    this.allLoadedReports.push(r);
                }
            });
        }
        this.resetIsBusyForReports();
        this.importReports(this.allLoadedReports ? this.allLoadedReports : reports);
    }

    // 5 seconds for 1 million or less docs, 15 seconds for 1 to 5 million docs, 30 seconds for higher
    startReportsPoller(request: any): void {
        let pollInterval = 5000; // 5 sec
        if (this.node && this.node.data && this.node.data.documentCount > 1000000) {
            pollInterval = this.node.data.documentCount > 5000000 ? 30000 : 15000;
        }
        this.getReportPoller = interval(pollInterval).pipe(
            startWith(0)
        ).subscribe(() => this.getReport(request, true));
    }

    stopReportsPoller(): void {
        if (this.getReportPoller) {
            this.getReportPoller.unsubscribe();
            this.getReportPoller = null;
        }
    }

    resetIsBusyForReports(): void {
        if (this.loadingReportsIsBusy) {
            this.loadingReportsIsBusy.forEach((value: boolean, key: ReportType) => {
                this.loadingReportsIsBusy.set(key, false);
            });
        }
    }
    importReports(rawReports: any[]): void {
        let unorderedReports = [];
        const convertedDates = [];
        let visibleReports = [];
        if (this.reportsVisibleList && this.reportsVisibleList.length > 0) {
            visibleReports = this.reportsVisibleList;
        } else {
            const hasCustodianDedup = this.commonService.currentProject.deDupScope === DeDupScope.VERTICAL;
            ReportMap.forEach((value: any, key: ReportType) => {
                const reportData = ReportMap.get(ReportType[ReportType[key]])(null, this.node);
                if ((hasCustodianDedup && key === ReportType.TECHNICAL_REDUCTION_VERTICAL) ||
                    (!hasCustodianDedup && key === ReportType.TECHNICAL_REDUCTION)) {
                    visibleReports.push(reportData.reportType);
                } else if (reportData.visible && key !== ReportType.EXPORT_COMPARE_STATISTICS &&
                    key !== ReportType.TECHNICAL_REDUCTION && key !== ReportType.TECHNICAL_REDUCTION_VERTICAL) {
                    visibleReports.push(reportData.reportType);
                }
            });
        }

        _.each(visibleReports, (visibleReport) => {
            let rawReport = _.find(rawReports, (r) => (r.reportType === visibleReport
                || (r.reportType === ReportType.TERM_SEARCH_HIT_COUNT_VERTICAL && visibleReport === ReportType.TERM_SEARCH_HIT_COUNT)));

            // the reason for this if condition is that there is no EXPORT_COMPARE_STATISTICS report type in the backend's response.
            // EXPORT_COMPARE_STATICS's grid should use EXPORT_COMPARE's reportEntryList.
            if (visibleReport === ReportType.EXPORT_COMPARE_STATISTICS) {
                rawReport = _.find(rawReports, (r) => (r.reportType === ReportType.EXPORT_COMPARE));
                if (rawReport) {
                    rawReport.reportType = ReportType.EXPORT_COMPARE_STATISTICS;
                }
            }
            if (!rawReport) {
                rawReport = { reportType: visibleReport };
            }
            if (rawReport.dateUpdated && rawReport.dateUpdated !== '') {
                // get the correct format for date
                const datetime = rawReport.dateUpdated.split('-');
                const convertedDate = new Date(datetime[0] + '-' + datetime[1] + '-' + datetime[2] + 'T' + datetime[3] + ':' + datetime[4] + ':' + datetime[5] + 'Z');
                convertedDates.push(convertedDate);
            }

            if (ReportMap.has(ReportType[ReportType[rawReport.reportType]])) {

                let mappedReport = ReportMap.get(ReportType[ReportType[rawReport.reportType]])(rawReport ? new Report(rawReport) : null, this.node);

                if (this.loadingReportsIsBusy && this.loadingReportsIsBusy.has(mappedReport.reportType)) {
                    mappedReport.isBusy = this.loadingReportsIsBusy.get(mappedReport.reportType);
                }
                if (mappedReport.indexLookUp && mappedReport.indexLookUp.length > 0) {
                    mappedReport.index = this.getIndex(mappedReport.indexLookUp);
                }
                if (mappedReport.reportDisplayType === ReportDisplayType.GRID && mappedReport.reportType !== ReportType.EXPORT_COMPARE_STATISTICS &&
                    mappedReport.reportType !== ReportType.TECHNICAL_REDUCTION) {
                    this.formatSizeColumns(mappedReport);
                }
                if (mappedReport.reportType === ReportType.SEARCH) {
                    mappedReport = this.proccesSearchSummaryData(mappedReport);
                } else if (mappedReport.reportType === this.summaryReport) {
                    mappedReport = this.proccesSearchResultData(mappedReport);
                } else if (mappedReport.reportType === ReportType.IMPORT) {
                    mappedReport = this.proccesScanSummaryData(mappedReport);
                } else if (mappedReport.reportType === ReportType.EXPORT_LOAD_FILE_REPORT || mappedReport.reportType === ReportType.EXPORT_PREPARE_REPORT
                    || mappedReport.reportType === ReportType.EXPORT_PRODUCE_REPORT) {
                    mappedReport = this.proccesExportVolumesData(mappedReport);
                } else if (mappedReport.reportType === ReportType.EXPORT_COMPARE) {

                    if (mappedReport.data.attributes && mappedReport.data.attributes.length > 0) {
                        let attr = _.find(mappedReport.data.attributes, (attribute) => attribute.name === 'exportstream');
                        this.exportComparisonExportNode = attr ? attr.value : '';

                        attr = _.find(mappedReport.data.attributes, (attribute) => attribute.name === 'searchresult');
                        this.exportComparisonSearchNode = attr ? attr.value : '';
                    }
                }
                if (mappedReport.reportType === ReportType.TECHNICAL_REDUCTION || mappedReport.reportType === ReportType.TECHNICAL_REDUCTION_VERTICAL
                    || mappedReport.reportType === this.summaryReport) {
                    unorderedReports.push(mappedReport);
                } else if (mappedReport.visible) {
                    unorderedReports.push(mappedReport);
                }
            }
        });

        const checkTodayDate = (date) => {
            const today = new Date();
            const dateFormatted = new Date(date);
            const diff = (today.getTime() - dateFormatted.getTime()) / 1000 / 3600;

            if (Math.abs(Math.round(diff)) <= 24) {
                return 'Today ' + date.slice(-8);
            }

            if ((Math.abs(Math.round(diff)) > 24 && Math.abs(Math.round(diff)) < 48)) {
                return 'Yesterday ' + date.slice(-8);
            }
            return date;
        };

        this.oldestReportDate = convertedDates.length > 0 ? 'Oldest report: ' + checkTodayDate(formatDate(new Date(Math.min.apply(null, convertedDates)), 'YYYY-MM-DD HH:mm:ss', null, false)) : ' ';
        unorderedReports = unorderedReports.map(report =>
            this.reportsDisabledList?.includes(report.reportType) ? { ...report, isDisabled: true } : report
        );
        if (!this.reports) {
            this.orderReports(unorderedReports);
        } else {
            if (unorderedReports) {
                _.each(rawReports, (newRep) => {
                    const idxReportToUpdate = this.reports.findIndex(r => r.reportType === newRep.reportType);
                    if (idxReportToUpdate > -1) {
                        const repWithData = unorderedReports.find(r => r.reportType === newRep.reportType);
                        if (repWithData) {
                            if (repWithData.reportDisplayType === ReportDisplayType.GRID) {
                                this.reports[idxReportToUpdate].visible = repWithData.visible;
                                this.reports[idxReportToUpdate].isBusy = repWithData.isBusy;
                                this.reports[idxReportToUpdate].data = repWithData.data;
                                if (repWithData.reportType === this.summaryReport && repWithData.data) {
                                    this.convertXmlData(this.reports[idxReportToUpdate]);
                                }
                            } else {
                                this.reports[idxReportToUpdate] = repWithData;
                            }
                        }
                    }
                });
            }
            this.isBusy = false;
        }
    }

    convertXmlData(reportEntry: any): void {
        if (reportEntry) {
            const data = reportEntry.data;
            if (reportEntry.clauseColumnName) {
                if (data.formatedData && data.formatedData.length > 0) {
                    data.formatedData.forEach(rawData => {
                        if (rawData[reportEntry.clauseColumnName]) {
                            if (reportEntry.reportType === ReportType.TERM_SEARCH_HIT_COUNT) {
                                const clauseValue = this.xmlService.xmlToQueryConverter(rawData[reportEntry.clauseColumnName].substring(1, rawData[reportEntry.clauseColumnName].length - 1), true) as any;
                                let clauseString = clauseValue.query && isString(clauseValue.query) ? clauseValue.query.replace(/</g, '&lt;').replace(/>/g, '&gt;') : clauseValue.query;
                                if (clauseValue.field) {
                                    const clauseStartField = clauseValue.field + '::';
                                    if (clauseString.startsWith(clauseStartField)) {
                                        const clauseStringWithoutField = clauseString.substring(clauseStartField.length, clauseString.length - 1).replace(/^\(|\)$/g, '');
                                        clauseString = clauseStartField + clauseStringWithoutField;
                                    } else {
                                        clauseString = `${clauseValue.field}::(${clauseString})`;
                                    }
                                }
                                rawData[reportEntry.clauseColumnName] = clauseValue && isString(clauseValue) ? this.xmlService.replaceWithEscapeChar(clauseValue.toString()) : clauseString;
                            } else {
                                const clauseValue = this.xmlService.xmlToQueryConverter(rawData[reportEntry.clauseColumnName].substring(1, rawData[reportEntry.clauseColumnName].length - 1));
                                rawData[reportEntry.clauseColumnName] = clauseValue && isString(clauseValue) ? this.xmlService.replaceWithEscapeChar(clauseValue.toString()) : clauseValue;
                            }
                        }
                    });
                }
                if (data.pinnedRow) {
                    data.pinnedRow[reportEntry.clauseColumnName] = this.xmlService.xmlToQueryConverter(data.pinnedRow[reportEntry.clauseColumnName]
                        .substring(1, data.pinnedRow[reportEntry.clauseColumnName].length - 1));
                }
            }
        }
    }

    getIndex(indexLookUp: any): number {
        if (this.node.name === this.dashboardName) {
            return indexLookUp.find(r => r.type === this.dashboardName).index;
        } else if (this.node.entityType === EntityType.projectData || this.node.entityType === EntityType.custodian || this.node.entityType === EntityType.mediaID || this.node.entityType === EntityType.batch
            || this.node.entityType === EntityType.tagView || this.node.entityType === EntityType.folder || this.node.entityType === EntityType.projectDataExport || this.node.entityType === EntityType.searchResult ||
            this.node.entityType === EntityType.savedSearch || this.node.entityType === EntityType.comparison) {
            return indexLookUp.find(r => r.type === EntityType.projectData).index;
        } else if (this.node.entityType === EntityType.import) {
            return indexLookUp.find(r => r.type === EntityType.import).index;
        }

        return 0;
    }

    orderReports(reports: any[]): void {
        this.reports = reports.sort((a, b) => {
            return a.index - b.index;
        });
        this.isBusy = false;
    }

    formatSizeColumns(report): void {
        if (report.data.formatedData && report.data.formatedData.length > 0) {
            report.data.formatedData.forEach(reportGridData => {
                if (reportGridData.size) {
                    reportGridData.unformattedSize = reportGridData.size;
                    reportGridData.size = this.commonService.formatSizeUnits(reportGridData.size);
                }
            });
        }
    }

    proccesSearchResultData(report): void {
        report.visible = this.searchSummarySearchType === ReportSearchType.FREEFORM || this.searchSummarySearchType === ReportSearchType.ADVANCED ||
            this.searchSummarySearchType === ReportSearchType.COMBINED || this.searchSummarySearchType === ReportSearchType.DRILL_THROUGH;
        if (report.data.formatedData && report.data.formatedData.length > 0) {
            report.visible = true;
        }
        return report;
    }

    proccesSearchSummaryData(report): void {
        if (report.data) {
            report.data.isBulkSearchNode = this.node.iconStringContent && this.node.iconStringContent === 'B' ? true : false;
            report.data.isWorkflowStepSearchResult = this.node?.data?.attributes?.find(a => a.name === 'workflow_handle') ? true : false;
            if (report.data.queryEntered) {
                if (report.data.queryEntered.indexOf('Combined_Search_') > -1) {
                    report.data.searchType = ReportSearchType.COMBINED;
                } else if (report.data.queryEntered.indexOf('cookie="DRAdvancedSearch"') > -1) {
                    report.data.searchType = ReportSearchType.ADVANCED;
                } else if (this.node && this.node.entityType === EntityType.searchResult && this.node.name && this.node.name.startsWith('Drill through')) {
                    report.data.searchType = ReportSearchType.DRILL_THROUGH;
                } else {
                    report.data.searchType = ReportSearchType.FREEFORM;
                }
                report.data.queryEntered = (this.xmlService.xmlToQueryConverter(report.data.queryEntered) as string).replace(new RegExp(/\( /g), '(').replace(new RegExp(/  \)/g), ')');
            }
            if (report.data.queryExecuted) {
                report.data.queryExecuted = this.xmlService.xmlToQueryConverter(report.data.queryExecuted);
            }
            this.searchSummarySearchType = report.data.searchType;
            if (this.node.data && this.node.data.task === SearchType.BULK && isArray(report.data.reportEntryList)) {
                report.data.formatedData = [];
                report.data.reportEntryList.forEach(childNode => {
                    if (childNode.name) {
                        childNode.documentCount = childNode.count ? parseInt(childNode.count, 10) : 0;
                        const clauseName = this.xmlService.xmlToQueryConverter(childNode.name);
                        const child = {
                            name: clauseName ? this.xmlService.replaceWithEscapeChar(clauseName.toString()) : clauseName,
                            data: childNode
                        };
                        report.data.formatedData.push(child);
                    }
                });
            }
        }
        return report;
    }

    proccesScanSummaryData(report: any): void {
        if (report && report.data && report.data.reportXML) {
            const xmlData = jquery.parseXML(report.data.reportXML);
            report.data.formatedData = [];
            if (xmlData) {
                if (xmlData.children && xmlData.children.length > 0) {
                    Array.from(xmlData.children).forEach((child: any) => {
                        Array.from(child.children).forEach((section: any, idx, sections) => {
                            const key: string = section.localName.replace(/[^\w\s]/gi, '').replace(/ /g, '');
                            if (key === 'section') {
                                Array.from(section.children).forEach((sectionChild: any) => {
                                    const keyNode: string = sectionChild.localName.replace(/[^\w\s]/gi, '').replace(/ /g, '');
                                    const val = isString(sectionChild.innerHTML) ? (sectionChild.innerHTML as string).trim() : '';
                                    if (keyNode === 'element') {
                                        const nodeData = { name: '', level: 1 };
                                        Array.from(sectionChild.children).forEach((nodeElement: any) => {
                                            const keyElement: string = nodeElement.localName.replace(/[^\w\s]/gi, '').replace(/ /g, '');
                                            const elementValue = isString(nodeElement.innerHTML) ? (nodeElement.innerHTML as string).trim() : '';
                                            nodeData[keyElement] = keyElement === 'size' ? this.commonService.formatSizeUnits(elementValue) : elementValue;
                                        });
                                        nodeData.level = nodeData.name.toLowerCase().indexOf('total') > -1 ? 0 : 1;
                                        report.data.formatedData.push(nodeData);
                                    } else {
                                        report.data.formatedData.push({
                                            name: val,
                                            count: '',
                                            size: '',
                                            isBold: true,
                                        });
                                    }
                                });

                                if (idx !== sections.length - 1) {
                                    report.data.formatedData.push({
                                        name: '',
                                        count: '',
                                        size: '',
                                        emptyRow: true,
                                    });
                                }
                            }
                        });
                    });
                }
            }
        }
        return report;
    }

    proccesExportVolumesData(report: any): void {
        if (report && report.data && report.data.reportXML) {
            const wbHandleAttribute = report.data.attributes ? report.data.attributes.find(a => a.name === 'wbHandle') : null;
            report.data.wbHandle = wbHandleAttribute?.value;
            const xmlData = jquery.parseXML(report.data.reportXML);
            report.data.formatedData = [];
            if (xmlData) {
                if (xmlData.children && xmlData.children.length > 0) {
                    Array.from(xmlData.children).forEach((child: any) => {
                        Array.from(child.children).forEach((section: any, idx, sections) => {
                            const key: string = section.localName.replace(/[^\w\s]/gi, '').replace(/ /g, '');
                            if (key === 'section') {
                                Array.from(section.children).forEach((sectionChild: any) => {
                                    const keyNode: string = sectionChild.localName.replace(/[^\w\s]/gi, '').replace(/ /g, '');
                                    const val = isString(sectionChild.innerHTML) ? (sectionChild.innerHTML as string).trim() : '';
                                    if (keyNode === 'element') {
                                        const nodeData = { name: '', level: 1, count: null, display: null, rowColor: false };
                                        Array.from(sectionChild.children).forEach((nodeElement: any) => {
                                            const keyElement: string = nodeElement.localName.replace(/[^\w\s]/gi, '').replace(/ /g, '');
                                            const elementValue = isString(nodeElement.innerHTML) ? (nodeElement.innerHTML as string).trim() : '';
                                            nodeData[keyElement] = elementValue;
                                        });

                                        if (nodeData.name.toLowerCase().indexOf('total') > -1) {
                                            nodeData.level = 0;
                                            switch (report.reportType) {
                                                case ReportType.EXPORT_PREPARE_REPORT:
                                                    nodeData.name = 'Total to be Produced';
                                                    break;
                                                case ReportType.EXPORT_LOAD_FILE_REPORT:
                                                    nodeData.name = 'Total Load File Entries';
                                                    break;
                                                case ReportType.EXPORT_PRODUCE_REPORT:
                                                    nodeData.name = 'Total Produced';
                                                    break;
                                            }
                                        } else {
                                            nodeData.level = 1;
                                        }

                                        nodeData.count = (nodeData.display === '-1' && nodeData.count !== '0') ?
                                            parseFloat(nodeData.count) * parseFloat(nodeData.display) : parseFloat(nodeData.count);
                                        report.data.formatedData.push(nodeData);
                                    } else {
                                        report.data.formatedData.push({
                                            name: val,
                                            count: '',
                                            isBold: true,
                                        });
                                    }
                                });

                                if (idx !== sections.length - 1) {
                                    report.data.formatedData.push({
                                        name: '',
                                        count: '',
                                        emptyRow: true,
                                    });
                                }
                            }
                        });
                    });
                }
            }
        }
        if (report.reportType === ReportType.EXPORT_PRODUCE_REPORT && report?.data?.formatedData?.length > 0) {
            report.data.formatedData.forEach(row => {
                if (row?.name?.indexOf('(unresolvable)') >= 0) {
                    row.actualCount = row.count;
                }
                if (row?.name?.indexOf('resolvable)') >= 0) {
                    row.showDrillHelper = true;
                }
            });
        }
        return report;
    }

    getReportListTypeFromEntityType(entityType: EntityType): ViewType.CORPUS_SET | ViewType.CORPUS | ViewType.WORKING_SET {
        switch (entityType) {
            case EntityType.import:
                return this.node.data instanceof Corpus ? ViewType.CORPUS : ViewType.CORPUS_SET;
            case EntityType.projectData:
            case EntityType.custodian:
            case EntityType.tagView:
            case EntityType.folder:
            case EntityType.comparison:
            case EntityType.mediaID:
            case EntityType.batch:
                return ViewType.WORKING_SET;
            case EntityType.searchResult:
                if (this.node.data.viewType === ViewType.CORPUS_RESULT) {
                    return ViewType.CORPUS;
                } else if (this.node.data.viewType === ViewType.CORPUS_SET_RESULT) {
                    return ViewType.CORPUS_SET;
                } else {
                    return ViewType.WORKING_SET;
                }
        }
        return ViewType.WORKING_SET;
    }

    getSearchReportListType(node: GenericTreeNode): ReportType[] {

        let isMetadataBulkSearch = false;
        let reportTypes = [];
        if (node.data && node.data.task === SearchType.BULK) {
            reportTypes.push(ReportType.SEARCH);
        } else {
            if (node.data && node.data.attributes) {
                const metadata = node.data.attributes.find(a => a.name === 'metadatabulksearch');
                const searchtype = node.data.attributes.find(b => b.name === 'searchtype');
                isMetadataBulkSearch = metadata || (searchtype && searchtype.value === 'Metadata Bulk Search');
            }
            if (!isMetadataBulkSearch && node.data && node.data.view && node.data.view.attributes) {
                const metadataBulkSearchAttribute = node.data.view.attributes.find(a => a.name === 'searchreporttype');
                if (metadataBulkSearchAttribute && metadataBulkSearchAttribute.value === 'METADATA_BULK_SEARCH') {
                    isMetadataBulkSearch = true;
                }
            }
            if (isMetadataBulkSearch) {
                reportTypes.push(ReportType.METADATA_BULK_SEARCH_SUMMARY);
            } else {
                reportTypes.push(ReportType.SEARCH, this.summaryReport, ReportType.MAG_HIT_COUNT);
            }

            if ((node.data && (node.data.viewType === ViewType.CORPUS_SET_RESULT || node.data.viewType === ViewType.CORPUS_RESULT))) {
                reportTypes.push(this.classificationReport, ReportType.FILETYPE, ReportType.DOCUMENT_CLASS, ReportType.CORPUS_SOURCE, ReportType.LANGUAGE,
                    ReportType.DOMINANT_LANGUAGE, ReportType.EMAIL_SENT_DOMAINS, ReportType.EMAIL_RECEIVED_DOMAINS, ReportType.EMAIL_SENDER, ReportType.EMAIL_RECEIVER,
                    ReportType.OCR_CONFIDENCE_LEVEL, ReportType.DATE_EMAIL_SENT, ReportType.DATE_EMAIL_RECEIVED, ReportType.CUSTODIAN_DIRECTORY_REPORT, ReportType.REVIEW_FOLDER);
            } else {
                reportTypes.push(ReportType.TAG_SUMMARY, this.classificationReport, ReportType.FILETYPE, ReportType.DOCUMENT_CLASS, ReportType.CORPUS_SOURCE, ReportType.LANGUAGE,
                    ReportType.DOMINANT_LANGUAGE, ReportType.EMAIL_SENT_DOMAINS, ReportType.EMAIL_RECEIVED_DOMAINS, ReportType.EMAIL_SENDER, ReportType.EMAIL_RECEIVER,
                    ReportType.GROUP_VIEW_MEMBERSHIP, ReportType.OCR_CONFIDENCE_LEVEL, ReportType.DATE_EMAIL_SENT, ReportType.DATE_EMAIL_RECEIVED, ReportType.DATE_PRIMARY, ReportType.REVIEW_FOLDER);
            }

            if (node.data && node.data.exportComparison && node.data.searchTermReportRequested) {
                reportTypes.push(ReportType.EXPORT_COMPARE);
            } else if (node.data && node.data.exportComparison) {
                reportTypes = [ReportType.EXPORT_COMPARE];
            }
        }
        return reportTypes;
    }

    formatReportRequest(reportList: string[]): Record<string, unknown> {

        if (this.node.data instanceof Comparison) {
            const comparisonView = _.filter(this.node.data, (viewItem) => viewItem && viewItem.handle === this.node.data.docListHandle);
            if (comparisonView && comparisonView.length > 0) {
                this.handle = comparisonView[0].handle;
            }
        }
        const preparedRequest = {
            viewHandle: this.handle,
            facetFilters: null,
            dateReportRequests: [],
            reportRequests: [],
            synchronousTimeout: 5
        };
        _.each(reportList, reportName => {
            switch (reportName) {
                case ReportType.DATE_EMAIL_SENT:
                case ReportType.DATE_EMAIL_RECEIVED:
                case ReportType.DATE_PRIMARY:
                    preparedRequest.dateReportRequests.push({
                        cachedOnly: false,
                        endDate: '',
                        granularity: 'YEAR',
                        reportType: reportName,
                        sortAscending: true,
                        sortBy: 'NAME',
                        startDate: ''
                    });
                    break;
                case ReportType.LANGUAGE:
                case ReportType.DOMINANT_LANGUAGE:
                case ReportType.EMAIL_SENDER:
                case ReportType.EMAIL_RECEIVER:
                case ReportType.GROUP_VIEW_MEMBERSHIP:
                case ReportType.OCR_CONFIDENCE_LEVEL:
                case ReportType.OCR_CANDIDATES:
                case ReportType.OCR_SOURCE:
                    preparedRequest.reportRequests.push({
                        cachedOnly: false,
                        reportType: reportName,
                        count: 10
                    });
                    break;
                case ReportType.BILLING_REPORT:
                case ReportType.CUSTODIAN_DIRECTORY_REPORT:
                case ReportType.TAG_SUMMARY:
                case ReportType.EMAIL_SENT_DOMAINS:
                case ReportType.EMAIL_RECEIVED_DOMAINS:
                    preparedRequest.reportRequests.push({
                        cachedOnly: false,
                        reportType: reportName,
                        count: 5
                    });
                    break;
                case ReportType.WORKFLOW:
                    preparedRequest.reportRequests.push({
                        reportType: reportName,
                    });
                    break;
                case ReportType.FILETYPE:
                    preparedRequest.reportRequests.push({
                        cachedOnly: false,
                        reportType: reportName,
                        attributes: [{ name: 'consolidated', value: 'true' }]
                    });
                    break;
                case ReportType.REVIEW_FOLDER:
                    preparedRequest.reportRequests.push({
                        cachedOnly: false,
                        reportType: reportName,
                        count: 25
                    });
                    break;
                default:
                    preparedRequest.reportRequests.push({
                        cachedOnly: false,
                        reportType: reportName
                    });
            }
        });
        return preparedRequest;
    }

    isBulkSearchButtonEnabled(isForAddTags: any): boolean {
        if (this.reportPanels && this.reportPanels.first && this.reportPanels.first.reportGrid) {
            const querySearches = this.reportPanels.first.reportGrid.selectedRows.filter(r => r.task !== 'COLLECTION_SIMILARITY' && r.task !== ' VECTOR_SET_FIND_DUPLICATES '
                && r.task !== 'BULK_SEARCH' && r.task !== 'METADATA_BULK_SEARCH');
            return isForAddTags ? querySearches.length >= 1 : querySearches.length >= 2;
        }
        return false;
    }

    runAsCombinedSearch(): void {
        if (this.reportPanels && this.reportPanels.first && this.reportPanels.first.reportGrid && this.reportPanels.first.reportGrid.selectedRows) {
            const querySearches = this.reportPanels.first.reportGrid.selectedRows.filter(r => r.task !== 'COLLECTION_SIMILARITY' && r.task !== ' VECTOR_SET_FIND_DUPLICATES '
                && r.task !== 'BULK_SEARCH' && r.task !== 'METADATA_BULK_SEARCH');
            if (querySearches) {
                const queriesToSend = [];
                querySearches.forEach(query => {
                    if (query && query.name) {
                        if (!query.name.startsWith('(')) {
                            query.name = `(${query.name})`;
                        }
                        queriesToSend.push(query.name.replace(/&lt;/g, '<').replace(/&gt;/g, '>'));
                    }
                });
                if (queriesToSend.length >= 2) {
                    this.commonService.openModal(RunCombinedSearchModalComponent, {
                        modalData: {
                            queries: queriesToSend,
                            searchTargetHandle: this.reportPanels.first.report.data.targetHandle,
                            searchOptions: {
                                includeFamily: this.reportPanels.first.report.data.includeFamilies,
                                includeMetadata: this.reportPanels.first.report.data.includeMetadata,
                                includeSynonyms: this.reportPanels.first.report.data.synonymExpansion
                            }
                        }
                    });
                } else {
                    this.commonService.openInformationToastNotification('Not all selected searches have queries. Please select other searches.');
                }
            }
        }
    }

    addTags(): void {
        if (this.reportPanels && this.reportPanels.first && this.reportPanels.first.reportGrid) {
            const selectedIndexes = [];
            const rows = this.reportPanels.first.reportGrid.getRows();
            for (let index = 0; index < rows.length; index++) {
                if (rows[index].isSelected()) {
                    selectedIndexes.push(index);
                }
            }
            this.restService.post(Endpoint.viewManager_listBulkSearchChildViews, {
                handle: this.handle,
                count: Math.max(...selectedIndexes) + 1,
                startIndex: 0
            }).then((response) => {
                const targets = [];
                for (let rowIndex = 0; rowIndex < selectedIndexes.length; rowIndex++) {
                    targets.push(response.views[selectedIndexes[rowIndex]]);
                }
                const updateStatusType = this.commonService.entityTypeToUpdateStatusType(this.node.entityType);
                const bulkTagging = (rows.length === targets.length || targets.length > 1) && this.node.data.task === 'BULK_SEARCH';
                this.commonService.openModal(TagsModalComponent, {
                    modalData: <TagsModalData>{
                        targets: targets,
                        targetType: bulkTagging ? ModalTargetType.bulk : ModalTargetType.view,
                        operationType: TagOperationType.tag,
                        updateStatusType: updateStatusType,
                        workBasketTaskHandle: bulkTagging ? [this.node.data.handle] : null
                    }
                });
            });
        }
    }

    updateDashboardReportsEnabled(): void {
        this.isBusy = true;
        this.isBusyMessage = 'Updating preferences';
        const payload = {
            ownerId: this.commonService.currentProject.handle,
            dashboardReportsEnabled: this.commonService.dashboardReportsEnabled,
        }
        if (this.commonService.preferences) {
            Object.assign(payload, this.commonService.preferences);
        }
        this.restService.post(Endpoint.projectManager_updatePreference, payload).then(response => {
            this.isBusy = false;
            this.loadData();
        }, error => {
            this.isBusy = false;
        });
    }

    openReportDetails(event: any): void {
        this.openDetailsModal(event.reportDetails);
    }

    public openDetailsModal(type: any): void {
        this.commonService.openModal(ReportsDetailsModalComponent, {
            modalData: <ReportsDetailsModalData>{
                detailType: type,
                handle: this.node.data.handle,
                title: type === DetailsType.EXCEPTION_DETAILS_IMPORT ? 'Exception Details' : 'Scan History',
                helpIndex: type === DetailsType.EXCEPTION_DETAILS_IMPORT ? HelpIndex.myedisc_scan_details_all_dlg : HelpIndex.scan_history_dlg
            },
            class: 'vertical-percent-100 horizontal-percent-100'
        });
    }

    openGenerateDataSetReportModal(): void {
        this.commonService.openModal(GenerateDataSetReportModalComponent, {
            modalData: <GenerateDataSetReportModalData>{
                viewHandle: this.handle
            }
        });
    }

    generateSlackProcessingReport(): void {
        this.restService.post(Endpoint.projectManager_generateDownloadFromWorkbasketReport, {
            projectHandle: this.commonService.contextHandle,
            reportType: ReportType.SLACK_DATA_REPORT,
            viewHandle: this.handle
        });
        this.commonService.openInformationToastNotification('Generation of slack processing report started. You can download the report from Work Basket panel.');
    }

    downloadFile(event: any): void {
        this.downloadReport(event.report, event.extension);
    }

    public downloadReport(report: any, extension: any): void {
        const endpoint = Endpoint.viewManager_retrieveReport,
            isBulkSearch = this.node && this.node.data && this.node.data.task === SearchType.BULK ? true : false;

        const attributes = [{ name: extension, value: 'true' }];
        const postParams = {
            handle: report === ReportType.WORKFLOW ? this.workflow.lastExecutionHandle : this.handle,
            reportRequest: { reportType: report === ReportType.SEARCH && !isBulkSearch ? ReportType.TERM_SEARCH_HIT_COUNT : report, attributes: attributes }
        };

        this.restService.post(endpoint, postParams).then(response => {
            if (response) {
                const url = response.url;
                this.commonService.unzipFile(url, this.downloadLink.nativeElement, report === ReportType.TECHNICAL_REDUCTION_VERTICAL ?
                    'TECHNICAL_REDUCTION_CUSTODIAL' : report);
            }
        }, error => {
            console.error('Failed to download!' + error);
        });
    }

    generateReports(): void {
        this.commonService.openModal(GenerateMoreReportsModalComponent, { modalData: { workflow: this.workflow } });
    }

    openHelp = (): void => {
        let helpIdx;
        if (this.node.name === this.dashboardName) {
            helpIdx = HelpIndex.case_dashboard_dlg;
        } else if (this.node.entityType === EntityType.workFlow) {
            helpIdx = HelpIndex.workflow_report_view_download_dlg;
        } else if (this.node.entityType === EntityType.searchResult || this.node.entityType === EntityType.savedSearch) {
            if (this.node && this.node.data && this.node.data.task === SearchType.BULK) {
                helpIdx = HelpIndex.myedisc_bulk_search_report_dlg;
            } else if (this.node && this.node.data && this.node.data.exportComparison) {
                helpIdx = HelpIndex.export_comparison_report_dlg;
            } else {
                helpIdx = HelpIndex.myedisc_search_results_report_dlg;
            }
        } else if (this.node.entityType === EntityType.import) {
            helpIdx = this.node.data instanceof Corpus ? HelpIndex.myedisc_scan_report_summary_dlg : HelpIndex.imports_reports_dlg;
        } else if (this.node.entityType === EntityType.projectData) {
            helpIdx = HelpIndex.myedisc_case_data_report_dlg;
        } else if (this.node.entityType === EntityType.custodian || this.node.entityType === EntityType.folder || this.node.entityType === EntityType.comparison ||
            this.node.entityType === EntityType.tagView || this.node.entityType === EntityType.mediaID || this.node.entityType === EntityType.batch) {
            helpIdx = HelpIndex.myedisc_per_custodian_report_dlg;
        } else if (this.node.entityType === EntityType.exportVolume) {
            helpIdx = HelpIndex.exports_ediscexports_vol_report_dlg;
        }

        this.commonService.openHelp(helpIdx);
    }

    ngOnDestroy(): void {
        this.stopReportsPoller();
    }
}
